import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, PaperPlaneTilt, User, Envelope, Phone, MapPin, ChatText, Warning, CheckCircle } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuotePage = () => {
  const { analysisId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedQuoteId, setSubmittedQuoteId] = useState(null);
  const [selectedParts, setSelectedParts] = useState(location.state?.selectedParts || []);
  const [selectedStockRefs, setSelectedStockRefs] = useState(location.state?.selectedStockRefs || []);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', email: '', phone: '', city: '', message: '' });

  useEffect(() => { fetchAnalysis(); }, [analysisId]);

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analyses/${analysisId}`);
      setAnalysis(response.data);
      if (!location.state?.selectedParts) setSelectedParts(response.data.detected_parts?.map(p => p.part_name) || []);
    } catch (error) {
      toast.error('Analyse non trouvée');
      navigate('/');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await axios.post(`${API_URL}/api/quotes`, { analysis_id: analysisId, selected_parts: selectedParts, selected_stock_refs: selectedStockRefs, ...formData });
      setSubmittedQuoteId(resp.data?.id || null);
      setSubmitted(true);
      toast.success('Demande envoyée !');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally { setSubmitting(false); }
  };

  const selectedPartDetails = analysis?.detected_parts?.filter(p => selectedParts.includes(p.part_name)) || [];
  // Build per-part Ecocasse stock total (if user picked specific stock refs for a part,
  // use the SUM of those Ecocasse prices for that part; else fall back to AI estimate).
  const stockTotalByPart = selectedStockRefs.reduce((acc, sr) => {
    acc[sr.part_name] = (acc[sr.part_name] || 0) + (Number(sr.prix_ttc) || 0);
    return acc;
  }, {});
  const partsWithStock = new Set(Object.keys(stockTotalByPart));
  const stockSubtotal = Object.values(stockTotalByPart).reduce((s, v) => s + v, 0);
  const estimateSubtotal = selectedPartDetails
    .filter(p => !partsWithStock.has(p.part_name))
    .reduce((sum, p) => sum + (p.estimated_market_price || p.price || 0), 0);
  const totalPrice = stockSubtotal + estimateSubtotal;

  if (loading) return <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center"><div className="w-12 h-12 border-2 border-[#E63946] border-t-transparent rounded-full animate-spin"></div></div>;

  if (submitted) return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Header />
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-gray-200 p-12 shadow-sm">
            <div className="w-20 h-20 bg-[#2A9D8F]/10 border border-[#2A9D8F]/30 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle size={40} weight="fill" className="text-[#2A9D8F]" /></div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-gray-900 mb-4">Demande envoyée !</h1>
            <p className="text-gray-500 mb-6">Un email de confirmation a été envoyé à <strong className="text-gray-900">{formData.email}</strong>.</p>
            {submittedQuoteId && (
              <div className="bg-[#E63946]/5 border border-[#E63946]/20 p-5 mb-6" data-testid="tracking-link-box">
                <p className="text-gray-700 text-sm mb-3">
                  🔗 <strong>Suivez l'avancement de votre demande</strong> en temps réel :
                </p>
                <Link
                  to={`/suivi/${submittedQuoteId}`}
                  className="inline-block bg-[#E63946] text-white px-6 py-3 text-xs font-bold uppercase tracking-wide hover:opacity-90 transition-opacity"
                  data-testid="tracking-link"
                >
                  Suivre ma demande →
                </Link>
                <p className="text-gray-400 text-[11px] mt-3">
                  Référence : <span className="font-mono">#{submittedQuoteId.slice(0, 8).toUpperCase()}</span>
                </p>
              </div>
            )}
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/" className="px-8 py-4 border border-gray-300 text-gray-700 text-sm font-bold uppercase hover:bg-gray-50">Retour à l'accueil</Link>
              <Link to="/analyse" className="px-8 py-4 bg-gray-900 text-white text-sm font-bold uppercase hover:opacity-90">Nouvelle analyse</Link>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <Header />
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Link to={`/resultats/${analysisId}`} className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-900 text-sm mb-6"><ArrowLeft size={16} />Retour aux résultats</Link>
            <h1 className="font-['Outfit'] text-2xl md:text-3xl font-bold text-gray-900">Demande de devis</h1>
            <p className="text-gray-500 mt-2">Remplissez le formulaire pour recevoir votre devis personnalisé.</p>
          </motion.div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white border border-gray-200 p-6 shadow-sm">
                  <h3 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><User size={20} className="text-[#E63946]" />Vos informations</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-gray-600 text-sm mb-2">Prénom <span className="text-[#E63946]">*</span></label><input type="text" value={formData.first_name} onChange={(e) => setFormData(p => ({ ...p, first_name: e.target.value }))} required className="w-full bg-white border border-gray-300 text-gray-900 p-4 focus:border-[#E63946]" data-testid="input-first-name" /></div>
                    <div><label className="block text-gray-600 text-sm mb-2">Nom <span className="text-[#E63946]">*</span></label><input type="text" value={formData.last_name} onChange={(e) => setFormData(p => ({ ...p, last_name: e.target.value }))} required className="w-full bg-white border border-gray-300 text-gray-900 p-4 focus:border-[#E63946]" data-testid="input-last-name" /></div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 p-6 shadow-sm">
                  <h3 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><Envelope size={20} className="text-[#E63946]" />Contact</h3>
                  <div className="space-y-4">
                    <div><label className="block text-gray-600 text-sm mb-2">Email <span className="text-[#E63946]">*</span></label><input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} required className="w-full bg-white border border-gray-300 text-gray-900 p-4 focus:border-[#E63946]" data-testid="input-email" /></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div><label className="block text-gray-600 text-sm mb-2">Téléphone <span className="text-[#E63946]">*</span></label><input type="tel" value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} required className="w-full bg-white border border-gray-300 text-gray-900 p-4 focus:border-[#E63946]" data-testid="input-phone" /></div>
                      <div><label className="block text-gray-600 text-sm mb-2">Ville</label><input type="text" value={formData.city} onChange={(e) => setFormData(p => ({ ...p, city: e.target.value }))} className="w-full bg-white border border-gray-300 text-gray-900 p-4 focus:border-[#E63946]" data-testid="input-city" /></div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 p-6 shadow-sm">
                  <h3 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-4 flex items-center gap-2"><ChatText size={20} className="text-[#E63946]" />Message (optionnel)</h3>
                  <textarea value={formData.message} onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))} rows={4} placeholder="Précisez vos besoins..." className="w-full bg-white border border-gray-300 text-gray-900 p-4 focus:border-[#E63946] resize-none" data-testid="input-message" />
                </div>

                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200"><Warning size={20} className="text-amber-600 flex-shrink-0" /><p className="text-gray-600 text-sm"><strong className="text-gray-900">Rappel :</strong> Cette analyse est indicative. Le devis définitif sera établi après vérification.</p></div>

                <button type="submit" disabled={submitting} className="lg:hidden w-full py-5 bg-[#E63946] text-white text-sm font-bold uppercase flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-quote-btn-mobile">{submitting ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Envoi...</> : <><PaperPlaneTilt size={20} />Envoyer</>}</button>
              </div>

              <div className="lg:col-span-1">
                <div className="bg-white border border-gray-200 p-6 sticky top-24 shadow-sm">
                  <h3 className="font-['Outfit'] text-lg font-bold text-gray-900 mb-4">Récapitulatif</h3>

                  {selectedStockRefs.length > 0 && (
                    <div className="mb-5 border border-[#E63946]/30 bg-[#E63946]/5 p-3" data-testid="quote-stock-section">
                      <p className="text-[#E63946] font-bold text-xs uppercase tracking-wide mb-2">
                        Stock Ecocasse sélectionné ({selectedStockRefs.length})
                      </p>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {selectedStockRefs.map((sr, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm border-b border-[#E63946]/10 pb-2 last:border-0" data-testid={`quote-stock-item-${idx}`}>
                            {sr.vignette ? (
                              <img src={sr.vignette} alt={sr.nom} className="w-10 h-10 object-cover border border-gray-200 flex-shrink-0" onError={(e)=>{e.target.style.display='none';}} />
                            ) : (
                              <div className="w-10 h-10 bg-gray-100 border border-gray-200 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-900 font-medium truncate text-xs">{sr.nom}</p>
                              <p className="text-gray-500 text-[10px] truncate">{sr.marque} {sr.modele}</p>
                            </div>
                            <span className="text-[#E63946] font-bold text-sm whitespace-nowrap ml-1">{Number(sr.prix_ttc).toFixed(2)} €</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between mt-3 pt-2 border-t border-[#E63946]/20">
                        <span className="text-gray-600 text-xs uppercase">Sous-total stock</span>
                        <span className="text-[#E63946] font-bold text-sm" data-testid="quote-stock-subtotal">{stockSubtotal.toFixed(2)} €</span>
                      </div>
                    </div>
                  )}

                  <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">Pièces ({selectedParts.length})</p>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto mb-4">
                    {selectedPartDetails.map((part, idx) => {
                      const hasStock = partsWithStock.has(part.part_name);
                      return (
                        <div key={idx} className="flex justify-between items-start text-sm">
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-900 truncate">{part.part_name}</p>
                            {part.side && <span className="text-gray-400 text-xs">{part.side}</span>}
                            {hasStock && <span className="block text-[#E63946] text-[10px] font-medium">Via stock Ecocasse</span>}
                          </div>
                          {!hasStock && (part.estimated_market_price || part.price) && (
                            <span className="text-[#2A9D8F] font-medium ml-2">{(part.estimated_market_price || part.price).toFixed(2)} €</span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-t border-gray-200 pt-4 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total estimé</span>
                      <span className="text-[#2A9D8F] font-bold text-xl" data-testid="quote-total">{totalPrice.toFixed(2)} €</span>
                    </div>
                    <p className="text-gray-400 text-xs mt-2">Prix indicatif TTC — devis définitif après vérification</p>
                  </div>
                  {(analysis?.vin || analysis?.brand || analysis?.model) && <div className="bg-gray-50 border border-gray-200 p-4 mb-6"><p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Véhicule</p>{analysis.vin && <p className="text-gray-900 font-medium font-mono text-sm">VIN : {analysis.vin}</p>}{(analysis.brand || analysis.model) && <p className="text-gray-600 text-sm">{analysis.brand} {analysis.model} {analysis.year}</p>}</div>}
                  <button type="submit" disabled={submitting} onClick={handleSubmit} className="hidden lg:flex w-full py-4 bg-[#E63946] text-white text-sm font-bold uppercase items-center justify-center gap-2 hover:bg-[#D90429] disabled:opacity-50" data-testid="submit-quote-btn">{submitting ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Envoi...</> : <><PaperPlaneTilt size={20} />Envoyer</>}</button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default QuotePage;
